/* loot.nu "Clean" — core theme behaviour (assets/loot.js). Vanilla JS, no dependencies.
 *
 * This file is the behaviour contract: markup opts in with custom elements + data-* hooks.
 * Globals from layout/theme.liquid: window.routes, window.lootStrings, #LootLiveRegion.
 * API: window.Loot.{openDialog, closeDialog, cart.{add,change,refresh,open}, setCartCount,
 *      announce, toast, fetchSection, debounce, fillCount, tagIndex, closePanels, init}
 *
 * DIALOGS   <dialog id="X" class="drawer|modal" data-light-dismiss data-exclusive aria-label>
 *             <div class="drawer__panel" data-dialog-panel>…</div>   (click outside the panel closes)
 *           openers [data-open-dialog="X"] (+ aria-controls="X" aria-haspopup="dialog"),
 *           closers [data-close-dialog]; Esc closes; focus returns to the opener;
 *           closing sets [data-closing] for 220 ms; [autofocus] inside is focused on open.
 *           Ids: MenuDrawer, SearchDialog (mobile search sheet), CartDrawer (layout), FilterDrawer.
 * CART      sections re-rendered on change: <div data-cart-section="{{ section.id }}">
 *           (cart-drawer: data-cart-section="cart-drawer" data-cart-count-value="{{ cart.item_count }}");
 *           badges [data-cart-count] (hidden at 0) + [data-cart-count-label]; header link [data-cart-open];
 *           lines <li data-cart-line="{{ forloop.index }}">, qty [data-qty] > [data-qty-step="-1|1"] +
 *           <input name="updates[]" data-line min max>, remove [data-cart-remove][data-line], error [data-cart-error].
 *           body[data-cart-type-page] → cart opens /cart instead of the drawer.
 * QTY       any [data-qty] > [data-qty-step] + input (respects min/max; dispatches change).
 * PRODUCT   root [data-product-section]; <product-form [data-purchasable="false"] [data-upcoming]>
 *           wraps the /cart/add form (hidden name="id", submit, [data-form-error]) — submit is
 *           refused while data-purchasable="false" (price 0, Kommande, sold out);
 *           <variant-picker data-section="{{ section.id }}"> + <script type="application/json"> variants
 *           → re-renders [data-variant-update="price|buttons|badges|inventory|sku|…"], sets ?variant=,
 *           data-purchasable, shows the variant media, announces strings.mediaShown;
 *           <media-gallery> [data-gallery-track] > [data-media-id], thumbs [data-gallery-thumb="id"]
 *           (aria-current + .is-active), [data-gallery-step="-1|1"], [data-gallery-counter].
 * BUY BAR   <div class="buybar" data-sticky-bar="BuyFormId" inert> → .is-shown + [data-visible]
 *           once #BuyFormId has scrolled above the viewport.
 * SEARCH    <predictive-search [data-predictive-popover]> > form[role=search] >
 *           input[type=search][role=combobox][aria-controls="PredictiveResults"][aria-expanded],
 *           [data-search-clear], #PredictiveResults[data-predictive-results] (filled from
 *           sections/predictive-search.liquid > [data-predictive-inner], items [data-predictive-item][role=option]),
 *           [data-predictive-status], optional [data-predictive-zero] (zero-query panel, hidden;
 *           shown on focus with an empty field; its items may carry [data-predictive-item] too).
 *           Popover: Esc / outside click / focus leaving closes; [data-open] on the host while open.
 * FACETS    [data-facets-root="{{ section.id }}"] > <facet-form data-section> > form (native filter inputs),
 *           groups <details data-facet="key">, links [data-facet-link], sort select[data-sort-select],
 *           grid [data-product-grid], count [data-results-count], pager [data-pagination] > a[data-load-more]
 *           (+ [data-more-label] "Visar x av n produkter", announced after loading).
 * RAILS     [data-rail-scope] > [data-rail] (scroll-snap track) + button[data-rail-step="-1|1"]
 *           (hidden at the ends; scope gets [data-rail-overflow]).
 * CAROUSEL  [data-carousel-scope] > [data-carousel] track + button[data-carousel-dot] (aria-current).
 * PANELS    button[data-panel-toggle][aria-controls][aria-expanded] ↔ [data-panel][hidden] (header Sortiment/Set).
 * EXPAND    button[data-expand] in [data-collapsed] (or aria-controls="ID") → [data-expanded];
 *           list search input[data-list-filter] in [data-list-filter-scope] over [data-list-filter-item].
 * COUNTS    [data-tag-count="Tag"] (+ data-tag-count-type, -one, -other; [data-tag-empty-hide]).
 * SHARE     [data-share="url?"] (Web Share, copy fallback), [data-copy-link="url?"] → toast strings.linkCopied.
 * HEADER    [data-header] (+ data-hide-on-scroll) → .is-stuck / [data-scrolled] / [data-hidden], --header-h.
 * OTHER     [data-reveal] → .is-visible (hidden only under html.js).
 * EDITOR    shopify:section:load re-inits; section select opens a dialog inside [data-editor-open].
 */
(() => {
  const Loot = (window.Loot = window.Loot || {});
  const routes = window.routes || {};
  const strings = window.lootStrings || {};
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  const debounce = (fn, wait = 300) => {
    let t;
    return (...args) => {
      clearTimeout(t);
      t = setTimeout(() => fn(...args), wait);
    };
  };

  const parseHTML = (html) => new DOMParser().parseFromString(html, 'text/html');

  const fetchSection = async (url, sectionId) => {
    const u = new URL(url, window.location.origin);
    u.searchParams.set('section_id', sectionId);
    const res = await fetch(u.toString(), { headers: { Accept: 'text/html' } });
    if (!res.ok) throw new Error(`Section ${sectionId} ${res.status}`);
    return parseHTML(await res.text());
  };

  const announce = (message) => {
    const region = document.getElementById('LootLiveRegion');
    if (!region || !message) return;
    region.textContent = '';
    requestAnimationFrame(() => (region.textContent = message));
  };

  Loot.debounce = debounce;
  Loot.fetchSection = fetchSection;
  Loot.announce = announce;

  /* ------------------------------------------------------------------ */
  /* Dialogs: [data-open-dialog="ID"], [data-close-dialog], backdrop click */
  /* ------------------------------------------------------------------ */
  // Opener per dialog, so focus returns to the right place. When a dialog is opened from inside
  // another exclusive dialog (MenuDrawer > "Sök" > SearchDialog), the closed dialog's own opener
  // is inherited instead of the button that disappears with it.
  const triggers = new WeakMap();

  Loot.openDialog = (id, trigger) => {
    const dialog = document.getElementById(id);
    if (!dialog || dialog.open) return dialog;
    let opener = trigger || document.activeElement;
    document.querySelectorAll('dialog[open][data-exclusive]').forEach((d) => {
      if (d === dialog) return;
      if (opener && d.contains(opener)) opener = triggers.get(d) || null;
      triggers.delete(d); // closed because another dialog opens: no focus return for it
      d.close();
    });
    triggers.set(dialog, opener);
    dialog.showModal();
    dialog.dispatchEvent(new CustomEvent('loot:dialog-open', { bubbles: true }));
    const autofocus = dialog.querySelector('[autofocus]');
    if (autofocus) autofocus.focus();
    return dialog;
  };

  Loot.closeDialog = (dialog) => {
    if (!dialog || !dialog.open) return;
    dialog.setAttribute('data-closing', '');
    const finish = () => {
      dialog.removeAttribute('data-closing');
      dialog.close();
    };
    if (reducedMotion.matches) finish();
    else setTimeout(finish, 220);
  };

  // A text field that opens a dialog (mobile search pill → SearchDialog): Enter, ArrowDown or a typed
  // character opens it too (keyboard / zoomed desktop), and the character carries over to the sheet's field.
  document.addEventListener('keydown', (event) => {
    const opener = event.target.closest && event.target.closest('input[data-open-dialog]');
    if (!opener || event.isComposing || event.ctrlKey || event.metaKey || event.altKey) return;
    const char = event.key.length === 1 ? event.key : '';
    if (!char && event.key !== 'Enter' && event.key !== 'ArrowDown') return;
    event.preventDefault();
    const dialog = Loot.openDialog(opener.getAttribute('data-open-dialog'), opener);
    const field = dialog && char && dialog.querySelector('input[type="search"]');
    if (field) {
      field.value += char;
      field.dispatchEvent(new Event('input', { bubbles: true }));
    }
  });

  document.addEventListener('click', (event) => {
    const opener = event.target.closest('[data-open-dialog]');
    if (opener) {
      event.preventDefault();
      Loot.openDialog(opener.getAttribute('data-open-dialog'), opener);
      return;
    }
    const closer = event.target.closest('[data-close-dialog]');
    if (closer) {
      event.preventDefault();
      Loot.closeDialog(closer.closest('dialog'));
      return;
    }
    // Light-dismiss dialogs are full-viewport and transparent; the visible part is
    // [data-dialog-panel], so a click whose target is the dialog itself hit the backdrop.
    if (event.target instanceof HTMLDialogElement && event.target.hasAttribute('data-light-dismiss')) {
      Loot.closeDialog(event.target);
    }
  });

  document.addEventListener(
    'cancel',
    (event) => {
      if (event.target instanceof HTMLDialogElement) {
        event.preventDefault();
        Loot.closeDialog(event.target);
      }
    },
    true
  );

  document.addEventListener(
    'close',
    (event) => {
      const dialog = event.target;
      if (!(dialog instanceof HTMLDialogElement) || !triggers.has(dialog)) return;
      let target = triggers.get(dialog);
      triggers.delete(dialog);
      // The opener may have been re-rendered (facets, cart): fall back to a visible opener.
      if (!target || !document.contains(target)) {
        target = [...document.querySelectorAll(`[data-open-dialog="${CSS.escape(dialog.id)}"], [aria-controls="${CSS.escape(dialog.id)}"]`)].find(
          (el) => el.getClientRects().length && !el.closest('dialog:not([open])')
        );
      }
      if (target && !target.closest('dialog:not([open])')) target.focus({ preventScroll: true });
    },
    true
  );

  /* ------------------------------------------------------------------ */
  /* Cart                                                                 */
  /* ------------------------------------------------------------------ */
  const cartSectionIds = () => {
    const ids = new Set(['cart-drawer']);
    document.querySelectorAll('[data-cart-section]').forEach((el) => ids.add(el.getAttribute('data-cart-section')));
    return [...ids];
  };

  // Keyboard focus survives a cart re-render: the same control on the same line, else on the line that
  // took its place, else the stepper input (minus disabled at 1), else the first control in the section.
  const cartFocusSelector = (el) => {
    const line = el.closest('[data-cart-line]');
    const pre = line ? `[data-cart-line="${line.getAttribute('data-cart-line')}"] ` : '';
    if (el.hasAttribute('data-qty-step')) return `${pre}[data-qty-step="${el.getAttribute('data-qty-step')}"]`;
    if (el.hasAttribute('data-cart-remove')) return `${pre}[data-cart-remove]`;
    if (el.hasAttribute('data-close-dialog')) return '[data-close-dialog]';
    if (el.name) return `${pre}${el.tagName.toLowerCase()}[name="${el.name}"]`;
    if (el.matches('a[href]') && window.CSS && CSS.escape) return `${pre}a[href="${CSS.escape(el.getAttribute('href'))}"]`;
    return null;
  };
  const restoreCartFocus = (target, sel) => {
    let el = sel ? target.querySelector(sel) || target.querySelector(sel.replace(/^\[data-cart-line="\d+"\] /, '')) : null;
    if (el && el.disabled) el = (el.closest('[data-qty]') || target).querySelector('input:not([type="hidden"])');
    if (!el || el.disabled) el = target.querySelector('a[href], button:not([disabled]), input:not([type="hidden"]), textarea');
    if (el) el.focus({ preventScroll: true });
  };

  const renderCartSections = (sections) => {
    if (!sections) return;
    Object.entries(sections).forEach(([id, html]) => {
      if (!html) return;
      const doc = parseHTML(html);
      document.querySelectorAll(`[data-cart-section="${id}"]`).forEach((target) => {
        const source = doc.querySelector(`[data-cart-section="${id}"]`);
        if (!source) return;
        const active = document.activeElement;
        const hadFocus = active && active !== target && target.contains(active);
        const sel = hadFocus ? cartFocusSelector(active) : null;
        target.innerHTML = source.innerHTML;
        if (hadFocus) restoreCartFocus(target, sel);
      });
      const countSource = doc.querySelector('[data-cart-count-value]');
      if (countSource) Loot.setCartCount(Number(countSource.getAttribute('data-cart-count-value')));
    });
  };

  Loot.setCartCount = (count) => {
    document.querySelectorAll('[data-cart-count]').forEach((el) => {
      el.textContent = count > 99 ? '99+' : String(count);
      el.hidden = count < 1;
    });
    document.querySelectorAll('[data-cart-count-label]').forEach((el) => {
      const template = count === 1 ? strings.cartCountOne : strings.cartCountOther;
      if (template) el.textContent = template.replace('{{ count }}', count);
    });
  };

  Loot.cart = {
    async add(formData) {
      formData.append('sections', cartSectionIds().join(','));
      formData.append('sections_url', window.location.pathname);
      const res = await fetch(`${routes.cart_add_url}.js`, {
        method: 'POST',
        headers: { 'X-Requested-With': 'XMLHttpRequest', Accept: 'application/javascript' },
        body: formData,
      });
      const data = await res.json();
      if (!res.ok || data.status) throw new Error(data.description || data.message || strings.cartError);
      renderCartSections(data.sections);
      document.dispatchEvent(new CustomEvent('loot:cart-updated', { detail: data }));
      return data;
    },
    async change(line, quantity) {
      const res = await fetch(`${routes.cart_change_url}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ line, quantity, sections: cartSectionIds(), sections_url: window.location.pathname }),
      });
      const data = await res.json();
      if (!res.ok || data.status) throw new Error(data.description || data.message || strings.cartError);
      renderCartSections(data.sections);
      Loot.setCartCount(data.item_count);
      document.dispatchEvent(new CustomEvent('loot:cart-updated', { detail: data }));
      return data;
    },
    async refresh() {
      const res = await fetch(`${routes.cart_url}?sections=${cartSectionIds().join(',')}`);
      renderCartSections(await res.json());
    },
    open(trigger) {
      if (document.body.hasAttribute('data-cart-type-page')) {
        window.location.href = routes.cart_url;
        return;
      }
      Loot.openDialog('CartDrawer', trigger);
    },
  };

  // Quantity changes + removals anywhere inside a cart section.
  const lineBusy = (lineEl, busy) => {
    if (!lineEl) return;
    lineEl.toggleAttribute('aria-busy', busy);
    lineEl.classList.toggle('is-loading', busy);
  };

  const handleLineChange = async (control, quantity) => {
    const lineEl = control.closest('[data-cart-line]');
    const line = Number(control.getAttribute('data-line') || (lineEl && lineEl.getAttribute('data-cart-line')));
    if (!line) return;
    lineBusy(lineEl, true);
    try {
      await Loot.cart.change(line, quantity);
      announce(strings.cartUpdated);
    } catch (error) {
      lineBusy(lineEl, false);
      const errorEl = (control.closest('[data-cart-section]') || document).querySelector('[data-cart-error]');
      if (errorEl) {
        errorEl.textContent = error.message;
        errorEl.hidden = false;
      }
      announce(error.message);
    }
  };

  document.addEventListener('click', (event) => {
    const remove = event.target.closest('[data-cart-remove]');
    if (remove) {
      event.preventDefault();
      handleLineChange(remove, 0);
      return;
    }
    const step = event.target.closest('[data-qty-step]');
    if (step) {
      const wrapper = step.closest('[data-qty]');
      const input = wrapper && wrapper.querySelector('input');
      if (!input) return;
      event.preventDefault();
      const min = Number(input.min || 0);
      const max = input.max ? Number(input.max) : Infinity;
      const next = Math.min(max, Math.max(min, Number(input.value || 0) + Number(step.getAttribute('data-qty-step'))));
      if (next === Number(input.value)) return;
      input.value = next;
      input.dispatchEvent(new Event('change', { bubbles: true }));
    }
    const cartOpener = event.target.closest('[data-cart-open]');
    if (cartOpener && !document.body.hasAttribute('data-cart-type-page')) {
      event.preventDefault();
      Loot.cart.open(cartOpener);
    }
  });

  document.addEventListener(
    'change',
    debounce((event) => {
      const input = event.target.closest('[data-cart-line] [data-qty] input');
      if (input) handleLineChange(input, Number(input.value));
    }, 350)
  );

  // Cart note: save on change, so a quantity re-render does not drop a note that was typed but not submitted.
  document.addEventListener('change', (event) => {
    const note = event.target.closest('[data-cart-section] textarea[name="note"]');
    if (!note || !window.routes || !routes.cart_update_url) return;
    fetch(`${routes.cart_update_url}.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ note: note.value }),
    }).catch(() => {});
  });

  /* ------------------------------------------------------------------ */
  /* Product form                                                         */
  /* ------------------------------------------------------------------ */
  class ProductForm extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('form');
      if (!this.form) return;
      this.form.addEventListener('submit', (e) => this.onSubmit(e));
    }
    async onSubmit(event) {
      event.preventDefault();
      const button = this.form.querySelector('[type="submit"]');
      if (!button || button.getAttribute('aria-disabled') === 'true' || button.disabled) return;
      // Honesty rule: price 0 / Kommande / sold out is never purchasable, even if the markup is stale.
      if (this.getAttribute('data-purchasable') === 'false') return;
      const errorEl = this.querySelector('[data-form-error]');
      if (errorEl) errorEl.hidden = true;
      button.setAttribute('aria-busy', 'true');
      button.classList.add('is-loading');
      try {
        await Loot.cart.add(new FormData(this.form));
        announce(strings.addedToCart);
        Loot.cart.open(button);
      } catch (error) {
        if (errorEl) {
          errorEl.textContent = error.message;
          errorEl.hidden = false;
        }
        announce(error.message);
      } finally {
        button.removeAttribute('aria-busy');
        button.classList.remove('is-loading');
      }
    }
  }
  customElements.define('product-form', ProductForm);

  /* ------------------------------------------------------------------ */
  /* Variant picker (re-renders [data-variant-update] blocks)            */
  /* ------------------------------------------------------------------ */
  class VariantPicker extends HTMLElement {
    connectedCallback() {
      const json = this.querySelector('script[type="application/json"]');
      this.variants = json ? JSON.parse(json.textContent) : [];
      this.addEventListener('change', () => this.onChange());
    }
    selectedOptions() {
      return [...this.querySelectorAll('select, input[type="radio"]:checked')].map((el) => el.value);
    }
    async onChange() {
      const options = this.selectedOptions();
      const variant = this.variants.find((v) => v.options.every((o, i) => o === options[i]));
      const section = this.closest('[data-product-section]');
      const idInput = section && section.querySelector('form[action*="/cart/add"] input[name="id"]');
      const productForm = section && section.querySelector('product-form');
      if (!variant) {
        section && section.querySelectorAll('[data-variant-update="buttons"] [type="submit"]').forEach((b) => b.setAttribute('aria-disabled', 'true'));
        if (productForm) productForm.setAttribute('data-purchasable', 'false');
        return;
      }
      if (idInput) idInput.value = variant.id;
      if (productForm) {
        const ok = variant.available && Number(variant.price) > 0 && !productForm.hasAttribute('data-upcoming');
        productForm.setAttribute('data-purchasable', String(ok));
      }
      const url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url.toString());
      const sectionId = this.getAttribute('data-section');
      if (!sectionId || !section) return;
      try {
        const doc = await fetchSection(url.toString(), sectionId);
        section.querySelectorAll('[data-variant-update]').forEach((el) => {
          const key = el.getAttribute('data-variant-update');
          const fresh = doc.querySelector(`[data-variant-update="${key}"]`);
          if (fresh) el.innerHTML = fresh.innerHTML;
        });
        // Option availability depends on the other selected options: sync classes + labels, keep the radios (and focus).
        const freshPicker = doc.querySelector('variant-picker');
        if (freshPicker) {
          this.querySelectorAll('.variant__opt input').forEach((input) => {
            const twin = freshPicker.querySelector(`input[name="${CSS.escape(input.name)}"][value="${CSS.escape(input.value)}"]`);
            const label = input.closest('.variant__opt');
            if (!twin || !label) return;
            label.classList.toggle('is-unavailable', twin.closest('.variant__opt').classList.contains('is-unavailable'));
            if (twin.hasAttribute('aria-label')) input.setAttribute('aria-label', twin.getAttribute('aria-label'));
            else input.removeAttribute('aria-label');
          });
        }
        if (variant.featured_media) {
          const gallery = section.querySelector('media-gallery');
          if (gallery && gallery.showMedia(String(variant.featured_media.id))) {
            const alt = variant.featured_media.alt || (variant.featured_media.preview_image && variant.featured_media.preview_image.alt);
            if (alt && strings.mediaShown) announce(strings.mediaShown.replace('{{ alt }}', alt));
          }
        }
      } catch (e) {
        /* keep the current UI if the re-render fails */
      }
    }
  }
  customElements.define('variant-picker', VariantPicker);

  /* ------------------------------------------------------------------ */
  /* Media gallery: scroll-snap track + thumbnails                       */
  /* ------------------------------------------------------------------ */
  class MediaGallery extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-gallery-track]');
      this.thumbs = [...this.querySelectorAll('[data-gallery-thumb]')];
      this.slides = this.track ? [...this.track.querySelectorAll('[data-media-id]')] : [];
      this.thumbs.forEach((thumb) =>
        thumb.addEventListener('click', () => this.showMedia(thumb.getAttribute('data-gallery-thumb')))
      );
      this.querySelectorAll('[data-gallery-step]').forEach((btn) =>
        btn.addEventListener('click', () => this.step(Number(btn.getAttribute('data-gallery-step'))))
      );
      if (this.track && 'IntersectionObserver' in window) {
        const io = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (entry.isIntersecting && entry.intersectionRatio > 0.6) this.setActive(entry.target.getAttribute('data-media-id'));
            });
          },
          { root: this.track, threshold: [0.6] }
        );
        this.slides.forEach((s) => io.observe(s));
      }
    }
    currentIndex() {
      return Math.max(0, this.slides.findIndex((s) => s.getAttribute('data-media-id') === this.active));
    }
    step(delta) {
      if (!this.slides.length) return;
      const next = (this.currentIndex() + delta + this.slides.length) % this.slides.length;
      this.showMedia(this.slides[next].getAttribute('data-media-id'));
    }
    showMedia(id) {
      const slide = this.slides.find((s) => s.getAttribute('data-media-id') === id);
      if (!slide || !this.track) return false;
      this.track.scrollTo({ left: slide.offsetLeft - this.track.offsetLeft, behavior: reducedMotion.matches ? 'auto' : 'smooth' });
      this.setActive(id);
      return true;
    }
    setActive(id) {
      this.active = id;
      this.thumbs.forEach((t) => {
        const on = t.getAttribute('data-gallery-thumb') === id;
        t.setAttribute('aria-current', String(on));
        t.classList.toggle('is-active', on);
      });
      const counter = this.querySelector('[data-gallery-counter]');
      if (counter) counter.textContent = `${this.currentIndex() + 1} / ${this.slides.length}`;
    }
  }
  customElements.define('media-gallery', MediaGallery);

  /* ------------------------------------------------------------------ */
  /* Predictive search                                                    */
  /* ------------------------------------------------------------------ */
  class PredictiveSearch extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('input[type="search"]');
      this.results = this.querySelector('[data-predictive-results]');
      this.status = this.querySelector('[data-predictive-status]');
      // Optional zero-query panel ("Senaste set" + Kategorier), server-rendered and hidden.
      this.zero = this.querySelector('[data-predictive-zero]');
      // Header pill: results/zero float under the pill and close on Esc / outside click / focus leaving.
      this.isPopover = this.hasAttribute('data-predictive-popover');
      if (!this.input || !this.results) return;
      this.cache = new Map();
      this.input.addEventListener('input', debounce(() => this.search(), 220));
      this.input.addEventListener('input', () => !this.input.value.trim() && this.clear());
      // Focus handed back after Esc must not reopen the panel it just closed.
      this.input.addEventListener('focus', () => !this.refocusing && this.open());
      this.input.addEventListener('click', () => this.open());
      // On the element (not the input): after ArrowDown focus sits on a result, keep arrowing from there.
      this.addEventListener('keydown', (e) => this.onKeydown(e));
      this.input.addEventListener('keydown', (e) => {
        if (e.key !== 'Escape') return;
        // In the search sheet Esc closes the dialog right away (a filled input would only clear itself).
        const dialog = this.closest('dialog[open]');
        if (dialog && this.input.value) {
          e.preventDefault();
          Loot.closeDialog(dialog);
        } else if (this.isPopover && this.hasAttribute('data-open')) {
          e.preventDefault();
          this.close();
        }
      });
      this.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.isPopover && e.target !== this.input && this.hasAttribute('data-open')) {
          e.preventDefault();
          this.close();
          this.refocusing = true;
          this.input.focus();
          this.refocusing = false;
        }
      });
      if (this.isPopover) {
        this.addEventListener('focusout', (e) => {
          if (e.relatedTarget && !this.contains(e.relatedTarget)) this.close();
        });
        document.addEventListener('click', (e) => {
          if (!this.contains(e.target)) this.close();
        });
      }
      this.querySelector('[data-search-clear]')?.addEventListener('click', () => {
        this.input.value = '';
        this.clear();
        this.input.focus();
      });
      // Inline (search sheet): the zero-query panel is the resting state.
      if (!this.isPopover && !this.input.value.trim()) this.showZero(true);
    }
    hasResults() {
      return this.results.innerHTML.trim().length > 0;
    }
    showZero(show) {
      if (!this.zero) return;
      this.zero.hidden = !show;
    }
    setExpanded(open) {
      this.input.setAttribute('aria-expanded', String(open));
      this.toggleAttribute('data-open', open);
      if (open && this.isPopover) this.fitPopover();
    }
    // The popover starts at the pill's left edge; cap it at the header's right content edge so it
    // never runs past the viewport (no clipped panel, no horizontal page scrollbar at 1024–1150px).
    fitPopover() {
      const pop = this.querySelector('.predictive-pop');
      const wrap = this.closest('.wrap');
      if (!pop || !wrap || !this.offsetWidth) return;
      const edge = wrap.getBoundingClientRect().right - parseFloat(getComputedStyle(wrap).paddingRight);
      pop.style.maxWidth = `${Math.max(this.offsetWidth, Math.floor(edge - this.getBoundingClientRect().left))}px`;
    }
    open() {
      const q = this.input.value.trim();
      if (!q) {
        this.results.hidden = true;
        this.showZero(true);
        this.setExpanded(!!this.zero);
      } else if (this.hasResults()) {
        this.showZero(false);
        this.results.hidden = false;
        this.setExpanded(true);
      }
    }
    close() {
      this.results.hidden = true;
      if (this.isPopover) this.showZero(false);
      this.setExpanded(false);
      this.results.querySelectorAll('[aria-selected="true"]').forEach((el) => el.setAttribute('aria-selected', 'false'));
    }
    clear() {
      this.results.innerHTML = '';
      this.results.hidden = true;
      this.toggleAttribute('data-has-query', false);
      if (this.status) this.status.textContent = '';
      const focused = document.activeElement === this.input || !this.isPopover;
      this.showZero(focused);
      this.setExpanded(focused && !!this.zero);
    }
    async search() {
      const q = this.input.value.trim();
      this.toggleAttribute('data-has-query', q.length > 0);
      if (!q) return this.clear();
      const key = q.toLowerCase();
      this.setAttribute('aria-busy', 'true');
      try {
        let html = this.cache.get(key);
        if (html === undefined) {
          const params = new URLSearchParams({
            q,
            section_id: 'predictive-search',
            'resources[type]': 'product,collection,page,query',
            'resources[limit]': '6',
            'resources[limit_scope]': 'each',
          });
          const res = await fetch(`${routes.predictive_search_url}?${params}`);
          if (!res.ok) throw new Error(res.status);
          const doc = parseHTML(await res.text());
          const inner = doc.querySelector('[data-predictive-inner]');
          html = inner ? inner.innerHTML : '';
          this.cache.set(key, html);
        }
        if (this.input.value.trim().toLowerCase() !== key) return;
        this.results.innerHTML = html;
        this.showZero(false);
        // A popover only opens while the shopper is still in the field.
        const engaged = !this.isPopover || this.contains(document.activeElement);
        this.results.hidden = !engaged;
        this.setExpanded(engaged);
        const count = this.results.querySelectorAll('[data-predictive-item]:not([data-predictive-all])').length;
        if (this.status) this.status.textContent = count ? (strings.searchResults || '').replace('{{ count }}', count) : strings.searchNoResults || '';
      } catch (e) {
        this.results.innerHTML = '';
        this.results.hidden = true;
      } finally {
        this.removeAttribute('aria-busy');
      }
    }
    onKeydown(event) {
      if (!['ArrowDown', 'ArrowUp'].includes(event.key)) return;
      // A closed combobox reopens on the arrow keys (after Esc or an outside click).
      if (event.target === this.input && !this.hasAttribute('data-open')) this.open();
      const scope = !this.results.hidden ? this.results : this.zero && !this.zero.hidden ? this.zero : null;
      if (!scope || (event.target !== this.input && !scope.contains(event.target))) return;
      const items = [...scope.querySelectorAll('[data-predictive-item]')];
      if (!items.length) return;
      event.preventDefault();
      const focused = items.indexOf(document.activeElement);
      const current = focused > -1 ? focused : items.findIndex((el) => el.getAttribute('aria-selected') === 'true');
      const next =
        event.key === 'ArrowDown'
          ? (current + 1) % items.length
          : current < 0
            ? items.length - 1
            : (current - 1 + items.length) % items.length;
      items.forEach((el, i) => el.setAttribute('aria-selected', String(i === next)));
      items[next].focus();
    }
  }
  customElements.define('predictive-search', PredictiveSearch);

  /* ------------------------------------------------------------------ */
  /* Collection facets + sort + load more (Section Rendering API)        */
  /* ------------------------------------------------------------------ */
  class FacetForm extends HTMLElement {
    connectedCallback() {
      this.sectionId = this.getAttribute('data-section');
      this.form = this.querySelector('form');
      if (!this.form || !this.sectionId) return;
      const onChange = debounce(() => this.submit(), 450);
      const isLocal = (e) => e.target.closest && e.target.closest('[data-list-filter]');
      this.form.addEventListener('change', (e) => !isLocal(e) && onChange());
      this.form.addEventListener('input', (e) => !isLocal(e) && e.target.matches('input[type="number"]') && onChange());
      this.form.addEventListener('submit', (e) => {
        e.preventDefault();
        if (document.activeElement && document.activeElement.closest('[data-list-filter]')) return;
        this.submit();
      });
    }
    params() {
      const data = new FormData(this.form);
      const params = new URLSearchParams();
      for (const [k, v] of data.entries()) if (v !== '') params.append(k, v);
      const sort = document.querySelector('[data-sort-select]');
      // Keep a sort the shopper picked; don't write the default sort into every filter URL.
      const picked = new URLSearchParams(window.location.search).has('sort_by');
      if (sort && !params.has('sort_by') && sort.value && picked) params.set('sort_by', sort.value);
      // The default sort needs no parameter (the drawer radios always post one): keep filter URLs clean.
      const def = this.form.querySelector('[name="sort_by"][data-default]');
      if (def && params.get('sort_by') === def.value) params.delete('sort_by');
      return params;
    }
    submit() {
      const query = this.params().toString();
      const url = query ? `${window.location.pathname}?${query}` : window.location.pathname;
      FacetForm.render(url, this.sectionId);
    }
    static async render(url, sectionId, push = true) {
      const root = document.querySelector(`[data-facets-root="${sectionId}"]`);
      if (!root) return (window.location.href = url);
      root.setAttribute('aria-busy', 'true');
      try {
        const doc = await fetchSection(url, sectionId);
        const fresh = doc.querySelector(`[data-facets-root="${sectionId}"]`);
        if (!fresh) throw new Error('missing root');
        // Preserve which <details> were open (keyed per occurrence: sidebar + drawer share keys).
        const detailsKeys = (scope) => {
          const seen = {};
          return [...scope.querySelectorAll('details[data-facet]')].map((d) => {
            const key = d.getAttribute('data-facet');
            seen[key] = (seen[key] || 0) + 1;
            return { el: d, id: `${key}:${seen[key]}` };
          });
        };
        const before = detailsKeys(root);
        const open = before.filter((d) => d.el.open).map((d) => d.id);
        const closed = before.filter((d) => !d.el.open).map((d) => d.id);
        const active = document.activeElement && root.contains(document.activeElement) ? document.activeElement : null;
        // A <select> has no value attribute, so match it by name only; inputs by name + value.
        const activeSel =
          active && active.name
            ? `[name="${CSS.escape(active.name)}"]${
                active.tagName !== 'SELECT' && active.value ? `[value="${CSS.escape(active.value)}"]` : ''
              }`
            : null;
        FacetForm.swap(root, fresh);
        initTagCounts(root);
        detailsKeys(root).forEach((d) => {
          if (open.includes(d.id)) d.el.setAttribute('open', '');
          else if (closed.includes(d.id)) d.el.removeAttribute('open');
        });
        if (activeSel) {
          const openDialog = root.querySelector('dialog[open]');
          const target = (openDialog && openDialog.querySelector(activeSel)) || root.querySelector(activeSel);
          if (target) target.focus({ preventScroll: true });
        }
        // A removed chip / "Rensa" link had focus: land on the next chip or the results count, not <body>.
        if (active && !root.contains(document.activeElement)) {
          const openDialog = root.querySelector('dialog[open]');
          const next = openDialog
            ? openDialog.querySelector('[data-dialog-panel] input:not([disabled]), [data-close-dialog]')
            : root.querySelector('.chips a, [data-results-count]');
          if (next) {
            if (!next.matches('a, button, input, select')) next.setAttribute('tabindex', '-1');
            next.focus({ preventScroll: true });
          }
        }
        if (push) {
          // Give the entry we leave a facet state too, so Back to the unfiltered page re-renders.
          if (!(window.history.state && window.history.state.facetUrl)) {
            window.history.replaceState({ facetUrl: window.location.pathname + window.location.search, sectionId }, '', window.location.href);
          }
          window.history.pushState({ facetUrl: url, sectionId }, '', url);
        }
        const count = root.querySelector('[data-results-count]');
        if (count) announce(count.textContent.trim());
      } catch (e) {
        window.location.href = url;
      } finally {
        root.removeAttribute('aria-busy');
      }
    }
  }
  // Replace the facets root content, but keep any open <dialog> (e.g. #FilterDrawer, a direct
  // child of [data-facets-root]) in the document: only its [data-dialog-panel] is refreshed, so
  // the modal stays open while filters re-render.
  FacetForm.swap = (root, fresh) => {
    const keep = [...root.children].filter((el) => el.matches('dialog[open]') && el.id);
    if (!keep.length) {
      root.innerHTML = fresh.innerHTML;
      return;
    }
    [...root.childNodes].forEach((node) => !keep.includes(node) && node.remove());
    let last = null;
    [...fresh.childNodes].forEach((node) => {
      const kept = node.nodeType === 1 && node.id ? keep.find((d) => d.id === node.id) : null;
      if (kept) {
        const livePanel = kept.querySelector('[data-dialog-panel]');
        const freshPanel = node.querySelector('[data-dialog-panel]');
        if (livePanel && freshPanel) livePanel.innerHTML = freshPanel.innerHTML;
        last = kept;
      } else if (last) {
        last.after(node);
        last = node;
      } else {
        root.insertBefore(node, keep[0]);
      }
    });
  };
  customElements.define('facet-form', FacetForm);

  document.addEventListener('click', (event) => {
    const link = event.target.closest('[data-facet-link]');
    if (!link) return;
    const root = link.closest('[data-facets-root]');
    if (!root) return;
    event.preventDefault();
    FacetForm.render(link.href, root.getAttribute('data-facets-root'));
  });

  document.addEventListener('change', (event) => {
    const sort = event.target.closest('[data-sort-select]');
    if (!sort) return;
    const root = sort.closest('[data-facets-root]');
    const url = new URL(window.location.href);
    url.searchParams.set('sort_by', sort.value);
    url.searchParams.delete('page');
    if (root) FacetForm.render(url.pathname + url.search, root.getAttribute('data-facets-root'));
    else window.location.href = url.toString();
  });

  window.addEventListener('popstate', (event) => {
    if (event.state && event.state.facetUrl) FacetForm.render(event.state.facetUrl, event.state.sectionId, false);
  });

  document.addEventListener('click', async (event) => {
    const more = event.target.closest('[data-load-more]');
    if (!more) return;
    const root = more.closest('[data-facets-root]');
    const grid = root && root.querySelector('[data-product-grid]');
    if (!root || !grid) return;
    event.preventDefault();
    more.setAttribute('aria-busy', 'true');
    try {
      const sectionId = root.getAttribute('data-facets-root');
      const doc = await fetchSection(more.href, sectionId);
      const freshGrid = doc.querySelector('[data-product-grid]');
      const first = freshGrid && freshGrid.firstElementChild;
      if (freshGrid) grid.append(...freshGrid.children);
      const freshPager = doc.querySelector('[data-pagination]');
      const pager = root.querySelector('[data-pagination]');
      if (pager) pager.replaceWith(freshPager || document.createElement('span'));
      // The URL stays as it is: a ?page=N URL would reload (or Back) into that page alone under a
      // "Visar 72 av 92" label, with the earlier products gone.
      if (first) first.querySelector('a')?.focus({ preventScroll: true });
      // "Visar 48 av 92 produkter": the fresh pager carries it; on the last page it comes from [data-more-label] of the doc.
      const label = doc.querySelector('[data-more-label]');
      if (label) announce(label.textContent.trim());
    } catch (e) {
      window.location.href = more.href;
    }
  });

  /* ------------------------------------------------------------------ */
  /* Horizontal rails: [data-rail] with [data-rail-step="-1|1"] buttons  */
  /* ------------------------------------------------------------------ */
  document.addEventListener('click', (event) => {
    const btn = event.target.closest('[data-rail-step]');
    if (!btn) return;
    const scope = btn.closest('[data-rail-scope]') || btn.parentElement;
    const rail = scope && scope.querySelector('[data-rail]');
    if (!rail) return;
    const item = rail.firstElementChild;
    const gap = parseFloat(getComputedStyle(rail).columnGap) || 0;
    const width = item ? item.getBoundingClientRect().width + gap : rail.clientWidth * 0.8;
    const perPage = Math.max(1, Math.floor(rail.clientWidth / width));
    rail.scrollBy({ left: Number(btn.getAttribute('data-rail-step')) * width * perPage, behavior: reducedMotion.matches ? 'auto' : 'smooth' });
  });

  const updateRailButtons = (rail) => {
    const scope = rail.closest('[data-rail-scope]');
    if (!scope) return;
    const max = rail.scrollWidth - rail.clientWidth - 2;
    const hideStep = { '-1': rail.scrollLeft <= 2, 1: rail.scrollLeft >= max };
    const steps = [...scope.querySelectorAll('[data-rail-step]')];
    // Show first, then hide: a focused arrow that hides hands focus to the other, now visible, arrow.
    steps.forEach((b) => { if (!hideStep[b.dataset.railStep]) b.hidden = false; });
    steps.forEach((b) => {
      if (!hideStep[b.dataset.railStep]) return;
      if (b === document.activeElement) {
        const other = steps.find((o) => o.dataset.railStep !== b.dataset.railStep && !o.hidden);
        if (other) other.focus({ preventScroll: true });
      }
      b.hidden = true;
    });
    scope.toggleAttribute('data-rail-overflow', max > 0);
  };

  const initRails = (root = document) => {
    root.querySelectorAll('[data-rail]').forEach((rail) => {
      if (rail.dataset.railReady) return;
      rail.dataset.railReady = '1';
      rail.addEventListener('scroll', debounce(() => updateRailButtons(rail), 60), { passive: true });
      updateRailButtons(rail);
    });
  };
  window.addEventListener('resize', debounce(() => document.querySelectorAll('[data-rail]').forEach(updateRailButtons), 150));

  /* ------------------------------------------------------------------ */
  /* Reveal on scroll: [data-reveal] → .is-visible                        */
  /* ------------------------------------------------------------------ */
  const revealObserver =
    'IntersectionObserver' in window
      ? new IntersectionObserver(
          (entries) =>
            entries.forEach((entry) => {
              if (entry.isIntersecting) {
                entry.target.classList.add('is-visible');
                revealObserver.unobserve(entry.target);
              }
            }),
          { rootMargin: '0px 0px -8% 0px', threshold: 0.08 }
        )
      : null;
  const initReveal = (root = document) => {
    root.querySelectorAll('[data-reveal]:not(.is-visible)').forEach((el) => {
      if (!revealObserver || reducedMotion.matches) el.classList.add('is-visible');
      else revealObserver.observe(el);
    });
  };

  /* ------------------------------------------------------------------ */
  /* Header state: [data-header] gets data-scrolled / data-hidden         */
  /* ------------------------------------------------------------------ */
  const initHeader = () => {
    const header = document.querySelector('[data-header]');
    if (!header) return;
    let lastY = window.scrollY;
    const hide = header.hasAttribute('data-hide-on-scroll');
    const onScroll = () => {
      const y = window.scrollY;
      header.toggleAttribute('data-scrolled', y > 4);
      header.classList.toggle('is-stuck', y > 4);
      if (hide) {
        const menuOpen = document.querySelector('dialog[open]');
        header.toggleAttribute('data-hidden', !menuOpen && y > 240 && y > lastY + 4);
        if (y < lastY - 4) header.removeAttribute('data-hidden');
      }
      lastY = y;
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    const setHeight = () => document.documentElement.style.setProperty('--header-h', `${header.offsetHeight}px`);
    setHeight();
    window.addEventListener('resize', debounce(setHeight, 100));
  };

  /* ------------------------------------------------------------------ */
  /* Share: [data-share="URL?"] → Web Share API (falls back to copying)   */
  /*        [data-copy-link="URL?"] → copies the URL                      */
  /* Both toast + announce strings.linkCopied and set [data-copied] 2 s.  */
  /* ------------------------------------------------------------------ */
  let toastEl;
  let toastTimer;
  Loot.toast = (message) => {
    if (!message) return;
    if (!toastEl) {
      toastEl = document.createElement('div');
      toastEl.className = 'toast';
      toastEl.setAttribute('aria-hidden', 'true'); // the live region announces it
      document.body.append(toastEl);
    }
    toastEl.textContent = message;
    requestAnimationFrame(() => toastEl.classList.add('is-shown'));
    clearTimeout(toastTimer);
    toastTimer = setTimeout(() => toastEl.classList.remove('is-shown'), 2400);
    announce(message);
  };

  const copyLink = async (btn, url) => {
    await navigator.clipboard.writeText(url);
    Loot.toast(strings.linkCopied);
    btn.setAttribute('data-copied', '');
    setTimeout(() => btn.removeAttribute('data-copied'), 2000);
  };

  document.addEventListener('click', async (event) => {
    const share = event.target.closest('[data-share]');
    const copy = event.target.closest('[data-copy-link]');
    const btn = share || copy;
    if (!btn) return;
    event.preventDefault();
    const url = btn.getAttribute(share ? 'data-share' : 'data-copy-link') || window.location.href;
    try {
      if (share && navigator.share) await navigator.share({ title: document.title, url });
      else await copyLink(btn, url);
    } catch (e) {
      /* user cancelled / clipboard blocked */
    }
  });

  /* ------------------------------------------------------------------ */
  /* Template strings: "{{ count }}" or "[count]" tokens                  */
  /* ------------------------------------------------------------------ */
  const fillCount = (template, count) =>
    template ? String(template).replace(/\{\{\s*count\s*\}\}|\[count\]/g, count) : String(count);
  Loot.fillCount = fillCount;

  /* ------------------------------------------------------------------ */
  /* Per-tag product counts (progressive enhancement)                   */
  /*   <span data-tag-count="Twilight Masquerade" [data-tag-count-type="Boosterboxar"]           */
  /*         data-tag-count-one="{{ count }} produkt" data-tag-count-other="{{ count }} produkter"> */
  /*   Optional ancestor [data-tag-empty-hide] is hidden when the count is 0.                     */
  /*   Source: /collections/all/products.json?limit=250&page=N (until empty),                     */
  /*   cached in sessionStorage for 10 min (one index per tab, shared by all counters).           */
  /* ------------------------------------------------------------------ */
  const TAG_CACHE_KEY = 'loot:tag-index:v3';
  const TAG_CACHE_TTL = 10 * 60 * 1000;
  let tagIndexPromise = null;

  const buildTagIndex = async () => {
    const index = { counts: {}, byType: {} };
    const base = (routes.all_products_url || `${(routes.root || '/').replace(/\/$/, '')}/collections/all`).replace(/\/$/, '');
    for (let page = 1; page <= 40; page += 1) {
      const res = await fetch(`${base}/products.json?limit=250&page=${page}`, { headers: { Accept: 'application/json' } });
      if (!res.ok) throw new Error(`products.json ${res.status}`);
      const data = await res.json();
      const products = (data && data.products) || [];
      if (!products.length) break;
      products.forEach((product) => {
        const tags = Array.isArray(product.tags) ? product.tags : String(product.tags || '').split(',');
        new Set(tags.map((t) => String(t).trim().toLowerCase()).filter(Boolean)).forEach((tag) => {
          index.counts[tag] = (index.counts[tag] || 0) + 1;
          const type = String(product.product_type || '').trim().toLowerCase();
          if (type) {
            const byType = index.byType[type] || (index.byType[type] = {});
            byType[tag] = (byType[tag] || 0) + 1;
          }
        });
      });
      if (products.length < 250) break;
    }
    return index;
  };

  Loot.tagIndex = () => {
    if (tagIndexPromise) return tagIndexPromise;
    try {
      const cached = JSON.parse(window.sessionStorage.getItem(TAG_CACHE_KEY) || 'null');
      if (cached && Date.now() - cached.t < TAG_CACHE_TTL && cached.d) {
        tagIndexPromise = Promise.resolve(cached.d);
        return tagIndexPromise;
      }
    } catch (e) {
      /* storage unavailable */
    }
    tagIndexPromise = buildTagIndex()
      .then((index) => {
        try {
          window.sessionStorage.setItem(TAG_CACHE_KEY, JSON.stringify({ t: Date.now(), d: index }));
        } catch (e) {
          /* quota / private mode */
        }
        return index;
      })
      .catch(() => {
        tagIndexPromise = null;
        return null;
      });
    return tagIndexPromise;
  };

  const initTagCounts = (root = document) => {
    const els = [...root.querySelectorAll('[data-tag-count]')].filter((el) => !el.dataset.tagReady);
    if (!els.length) return;
    els.forEach((el) => (el.dataset.tagReady = '1'));
    Loot.tagIndex().then((index) => {
      if (!index) return;
      els.forEach((el) => {
        if (el.hasAttribute('data-tag-count')) {
          const tag = el.getAttribute('data-tag-count').trim().toLowerCase();
          // Optional data-tag-count-type="<product type>" scopes the count to one format.
          const scope = el.getAttribute('data-tag-count-type');
          const counts = scope ? (index.byType || {})[scope.trim().toLowerCase()] || {} : index.counts;
          const count = counts[tag] || 0;
          const template = count === 1 ? el.getAttribute('data-tag-count-one') : el.getAttribute('data-tag-count-other');
          el.textContent = fillCount(template, count);
          el.setAttribute('data-tag-count-value', String(count));
          if (count === 0) {
            const hideTarget = el.closest('[data-tag-empty-hide]');
            if (hideTarget) hideTarget.hidden = true;
          }
        }
      });
    });
  };

  /* ------------------------------------------------------------------ */
  /* Client-side option search + "show more" reveals                     */
  /*   <div data-list-filter-scope data-collapsed>                         */
  /*     <input type="search" data-list-filter>   (no name attr)          */
  /*     <label class="opt" data-list-filter-item [data-extra]>…</label>   */
  /*     <button type="button" class="fgroup__more" data-expand>Visa alla 24 set</button>     */
  /*   The button may sit outside its scope: data-expand + aria-controls="ID" (set grid       */
  /*   "Visa alla set"). Expanding removes [data-collapsed], adds [data-expanded], sets        */
  /*   aria-expanded="true", hides the button and focuses the first revealed                   */
  /*   ([data-extra] / .is-extra) link, input or button.                                      */
  /* ------------------------------------------------------------------ */
  document.addEventListener('input', (event) => {
    const input = event.target.closest && event.target.closest('[data-list-filter]');
    if (!input) return;
    const scope = input.closest('[data-list-filter-scope]');
    if (!scope) return;
    const q = input.value.trim().toLowerCase();
    scope.toggleAttribute('data-searching', q.length > 0);
    scope.querySelectorAll('[data-list-filter-item]').forEach((item) => {
      item.hidden = q.length > 0 && !item.textContent.toLowerCase().includes(q);
    });
  });
  document.addEventListener('click', (event) => {
    const btn = event.target.closest('[data-expand]');
    if (!btn) return;
    const controls = btn.getAttribute('aria-controls');
    const scope = (controls && document.getElementById(controls)) || btn.closest('[data-collapsed]');
    if (!scope) return;
    event.preventDefault();
    scope.removeAttribute('data-collapsed');
    scope.setAttribute('data-expanded', '');
    btn.setAttribute('aria-expanded', 'true');
    btn.hidden = true;
    const first = scope.querySelector(
      '[data-extra] input, [data-extra] a, [data-extra] button, .is-extra input, .is-extra a, .is-extra button'
    );
    if (first) first.focus({ preventScroll: true });
  });

  /* ------------------------------------------------------------------ */
  /* Sticky bar: <div data-sticky-bar="TargetId" inert> shows            */
  /* (data-visible) once #TargetId has scrolled above the viewport.      */
  /* ------------------------------------------------------------------ */
  const initStickyBars = (root = document) => {
    if (!('IntersectionObserver' in window)) return;
    root.querySelectorAll('[data-sticky-bar]').forEach((bar) => {
      if (bar.dataset.stickyReady) return;
      const target = document.getElementById(bar.getAttribute('data-sticky-bar'));
      if (!target) return;
      bar.dataset.stickyReady = '1';
      new IntersectionObserver(([entry]) => {
        const past = !entry.isIntersecting && entry.boundingClientRect.top < 0;
        bar.toggleAttribute('data-visible', past);
        bar.classList.toggle('is-shown', past);
        bar.inert = !past;
      }).observe(target);
    });
  };

  /* ------------------------------------------------------------------ */
  /* Header dropdown panels:                                             */
  /*   <button data-panel-toggle aria-expanded="false" aria-controls="panel-set">  */
  /*   <div id="panel-set" class="hdr__panel" data-panel hidden>…</div>  */
  /* One open at a time; Esc, an outside click or focus leaving closes   */
  /* it (Esc returns focus to its button). Opening a dialog closes them. */
  /* ------------------------------------------------------------------ */
  const panelToggles = () => [...document.querySelectorAll('[data-panel-toggle][aria-controls]')];
  const panelFor = (btn) => document.getElementById(btn.getAttribute('aria-controls'));
  Loot.closePanels = (except) => {
    panelToggles().forEach((btn) => {
      if (btn === except || btn.getAttribute('aria-expanded') !== 'true') return;
      btn.setAttribute('aria-expanded', 'false');
      const panel = panelFor(btn);
      if (panel) panel.hidden = true;
    });
  };
  document.addEventListener('click', (event) => {
    const btn = event.target.closest('[data-panel-toggle][aria-controls]');
    if (btn) {
      const panel = panelFor(btn);
      if (!panel) return;
      event.preventDefault();
      const open = btn.getAttribute('aria-expanded') !== 'true';
      Loot.closePanels(btn);
      btn.setAttribute('aria-expanded', String(open));
      panel.hidden = !open;
      return;
    }
    if (!event.target.closest('[data-panel]')) Loot.closePanels();
  });
  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;
    const open = panelToggles().find((b) => b.getAttribute('aria-expanded') === 'true');
    if (!open) return;
    Loot.closePanels();
    open.focus();
  });
  document.addEventListener('focusout', (event) => {
    const next = event.relatedTarget;
    if (!next) return;
    panelToggles().forEach((btn) => {
      if (btn.getAttribute('aria-expanded') !== 'true') return;
      const panel = panelFor(btn);
      const inside = btn.contains(next) || (panel && panel.contains(next));
      const leaving = btn.contains(event.target) || (panel && panel.contains(event.target));
      if (leaving && !inside) {
        btn.setAttribute('aria-expanded', 'false');
        if (panel) panel.hidden = true;
      }
    });
  });
  document.addEventListener('loot:dialog-open', () => Loot.closePanels());

  /* ------------------------------------------------------------------ */
  /* Carousel (hero): scroll-snap track, dots are an enhancement.        */
  /*   <div data-carousel-scope>                                         */
  /*     <div class="hero__track" data-carousel> <div class="hero__slide">… */
  /*     <div class="hero__dots"> <button class="hero__dot" data-carousel-dot aria-current="true"> */
  /* Dot i scrolls to slide i; aria-current follows the snapped slide.  */
  /* No autoplay.                                                        */
  /* ------------------------------------------------------------------ */
  const initCarousels = (root = document) => {
    root.querySelectorAll('[data-carousel]').forEach((track) => {
      if (track.dataset.carouselReady) return;
      track.dataset.carouselReady = '1';
      const scope = track.closest('[data-carousel-scope]') || track.parentElement;
      const dots = [...scope.querySelectorAll('[data-carousel-dot]')];
      const slides = [...track.children];
      const current = () => (track.clientWidth ? Math.round(track.scrollLeft / track.clientWidth) : 0);
      const sync = () => {
        const i = current();
        dots.forEach((d, j) => d.setAttribute('aria-current', String(i === j)));
        slides.forEach((slide, j) => slide.toggleAttribute('inert', j !== i && slides.length > 1));
      };
      dots.forEach((dot, j) =>
        dot.addEventListener('click', () =>
          track.scrollTo({ left: j * track.clientWidth, behavior: reducedMotion.matches ? 'auto' : 'smooth' })
        )
      );
      track.addEventListener('scroll', debounce(sync, 60), { passive: true });
      sync();
    });
  };

  /* ------------------------------------------------------------------ */
  /* Init + theme editor hooks                                            */
  /* ------------------------------------------------------------------ */
  const init = (root = document) => {
    initRails(root);
    initCarousels(root);
    initReveal(root);
    initTagCounts(root);
    initStickyBars(root);
  };
  Loot.init = init;

  // Scrolling category pill row (<1024): centre the current collection's pill so the gold cue is visible.
  const centreCurrentPill = () =>
    document.querySelectorAll('.cat-pills__list').forEach((list) => {
      const cur = list.querySelector('[aria-current="page"]');
      if (!cur || list.scrollWidth <= list.clientWidth) return;
      const l = list.getBoundingClientRect();
      const c = cur.getBoundingClientRect();
      list.scrollLeft += c.left - l.left - (l.width - c.width) / 2;
    });

  const boot = () => {
    initHeader();
    init();
    centreCurrentPill();
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();

  // Re-init after dynamic renders (facets, cart, predictive search, theme editor).
  new MutationObserver((mutations) => {
    for (const m of mutations) {
      for (const node of m.addedNodes) if (node.nodeType === 1) init(node.parentElement || document);
    }
  }).observe(document.documentElement, { childList: true, subtree: true });

  document.addEventListener('shopify:section:load', (e) => {
    init(e.target);
    if (e.target.querySelector('[data-header]')) initHeader();
  });
  document.addEventListener('shopify:section:select', (e) => {
    const dialog = e.target.querySelector('dialog');
    if (dialog && !dialog.open && e.target.hasAttribute('data-editor-open')) dialog.showModal();
  });
})();
